import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { ActivatedRoute } from '@angular/router'
import {
  DataContextBuilder,
  ElderBasicPaneLayoutComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderTabDirective,
  ElderTabGroupRoutingDirective,
  IDataContext,
  Sort,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, combineLatest, Observable } from 'rxjs'
import { filter, map, tap } from 'rxjs/operators'
import { isTerminalPackageUiEnabled } from '../../../../../../environments/environment'
import { WorkBatchState } from '../../../../orders/batches/work-batch'
import { TerminalBatchFilter, TerminalBatchService } from '../../batches/terminal-batch.service'
import { TerminalOrderPackagesComponent } from '../terminal-order-packages/terminal-order-packages.component'
import { TerminalWorkBatch } from '../terminal-work-batch'
import { TerminalWorkBatchesTableComponent } from '../terminal-work-batches-table/terminal-work-batches-table.component'

@Component({
  selector: 'app-terminal-order-batches',
  templateUrl: './terminal-order-batches.component.html',
  styleUrls: ['./terminal-order-batches.component.scss'],
  imports: [
    NgTemplateOutlet,
    MatTabGroup,
    MatTab,
    AsyncPipe,
    ElderTranslatePipe,
    ElderTabDirective,
    ElderTabGroupRoutingDirective,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
    ElderPaneHeaderComponent,
    TerminalWorkBatchesTableComponent,
    TerminalOrderPackagesComponent,
  ],
})
export class TerminalOrderBatchesComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  public readonly workCenterId$ = new BehaviorSubject<string>(null)
  public readonly workOrderId$ = new BehaviorSubject<string>(null)
  public readonly workStationId$ = new BehaviorSubject<string>(null)
  public readonly includeUnstartable$ = new BehaviorSubject<boolean>(false)
  public readonly currentTime$ = new BehaviorSubject<string>(null)

  public readonly data: IDataContext<TerminalWorkBatch>

  public WorkBatchState = WorkBatchState
  private readonly terminalBatchService = inject(TerminalBatchService)
  private route = inject(ActivatedRoute)
  isTerminalPackageUiEnabled = isTerminalPackageUiEnabled

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.data = DataContextBuilder.start<TerminalWorkBatch>()
      .pageSize(100)
      .build(this.terminalBatchService)

    combineLatest([
      this.workOrderId$,
      this.workCenterId$,
      this.workStationId$,
      this.includeUnstartable$,
      this.currentTime$, // hack to reload after ws config changes
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([orderId, workCenterId, workStationId, includeUnstartable, _]) => {
        const message = `order: ${orderId}, wc: ${workCenterId}, ws: ${workStationId}, includeUnstartable: ${includeUnstartable}`
        this.log.debug(message)

        if (orderId && workCenterId && workStationId) {
          this.data.start(
            [new Sort('number', 'asc')],
            [
              TerminalBatchFilter.workOrderId(orderId),
              TerminalBatchFilter.workCenterId(workCenterId),
              TerminalBatchFilter.workStationId(workStationId),
              TerminalBatchFilter.includeUnstartable(includeUnstartable),
            ]
          )
        }
      })

    this.route.parent.params
      .pipe(
        takeUntilDestroyed(),
        tap((parentParams) => {
          console.debug(`got parent params ${JSON.stringify(parentParams)}`)
        }),
        map((params) => params['workCenterId']),
        filter((id) => id)
      )
      .subscribe((workCenterId) => {
        this.workCenterId$.next(workCenterId)
      })

    this.route.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      console.debug(`params: ${JSON.stringify(params)}`)
      const orderId = params['workOrderId']
      if (orderId) {
        this.workOrderId$.next(orderId)
      }
    })

    this.route.queryParams.pipe(takeUntilDestroyed()).subscribe((queryParams) => {
      console.debug(`queryParams: ${JSON.stringify(queryParams)}`)
      const workStationId = queryParams['workStationId']
      if (this.workStationId$.value !== workStationId) {
        this.workStationId$.next(workStationId)
      }
      const includeUnstartable = coerceBooleanProperty(queryParams['includeUnstartable'])
      if (this.includeUnstartable$.value != includeUnstartable) {
        this.includeUnstartable$.next(includeUnstartable)
      }
      const currentTime = queryParams['currentTime']
      if (currentTime) {
        this.currentTime$.next(currentTime)
      }
    })
  }
}
